import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';

export type DsarRequestType = 'access' | 'export' | 'erasure';
export type DsarStatus = 'open' | 'in_progress' | 'completed' | 'closed';

export interface DsarRequestRow {
  id: number;
  subject_name: string;
  subject_email: string;
  tenant_id: number;
  tenant_name?: string;
  request_date: string;
  request_type: DsarRequestType;
  status: DsarStatus;
  notes: string | null;
  compiled_data_path: string | null;
  compiled_data_expires_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface DsarListPayload {
  tenantId?: number;
  requestType?: DsarRequestType;
  status?: DsarStatus;
  page?: number;
  limit?: number;
}

export interface DsarFormPayload {
  subject_name: string;
  subject_email: string;
  tenant_id: number;
  request_date: string;
  request_type: DsarRequestType;
  notes?: string;
}

const list = (payload: DsarListPayload) => {
  const { tenantId, requestType, status, page, limit } = payload;
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = String(tenantId);
  if (requestType) params.requestType = requestType;
  if (status) params.status = status;
  if (page) params.page = String(page);
  if (limit) params.limit = String(limit);
  const query = new URLSearchParams(params).toString();
  return http.get(`${API_ENDPOINTS.dsar}${query ? `?${query}` : ''}`);
};

const get = (id: number | string) => http.get(`${API_ENDPOINTS.dsar}/${id}`);

const create = (payload: DsarFormPayload) => http.post(API_ENDPOINTS.dsar, payload);

const updateStatus = (id: number | string, status: DsarStatus) =>
  http.patch(`${API_ENDPOINTS.dsar}/${id}/status`, { status });

const compile = (id: number | string) => http.post(`${API_ENDPOINTS.dsar}/${id}/compile`, {});

const downloadCompiled = (id: number | string) =>
  http.get(`${API_ENDPOINTS.dsar}/${id}/download`, { responseType: 'blob' });

const erase = (id: number | string, reason: string) => http.post(`${API_ENDPOINTS.dsar}/${id}/erasure`, { reason });

const standaloneErasure = (subjectEmail: string, tenantId: number, reason: string) =>
  http.post(`${API_ENDPOINTS.dsar}/standalone-erasure`, { subjectEmail, tenantId, reason });

const downloadCertificate = (erasureRecordId: number | string) =>
  http.get(`${API_ENDPOINTS.erasureRecords}/${erasureRecordId}/certificate`, { responseType: 'blob' });

const triggerComplianceExport = (tenantId: number | string) =>
  http.post(`${API_ENDPOINTS.tenants}/${tenantId}/compliance-export`, {});

const getComplianceExportStatus = (tenantId: number | string) =>
  http.get(`${API_ENDPOINTS.tenants}/${tenantId}/compliance-export/status`);

const downloadComplianceExport = (tenantId: number | string) =>
  http.get(`${API_ENDPOINTS.tenants}/${tenantId}/compliance-export/download`, { responseType: 'blob' });

const ComplianceService = {
  list,
  get,
  create,
  updateStatus,
  compile,
  downloadCompiled,
  erase,
  standaloneErasure,
  downloadCertificate,
  triggerComplianceExport,
  getComplianceExportStatus,
  downloadComplianceExport,
};

export default ComplianceService;
